import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAdminAuth } from "./AdminAuthContext";
import { useToast } from "./ToastProvider";
import {
  ShieldAlert, Shield, Download, RefreshCw, XCircle, LogOut,
  Monitor, Smartphone, Tablet, Globe, Filter, ChevronDown, ChevronRight, Clock,
} from "lucide-react";

const ACTIONS = [
  "any", "login", "logout", "login_failed", "password_reset", "session_revoked",
  "create_product", "update_product", "delete_product", "publish", "unpublish",
  "update_price", "update_inventory", "view_order", "update_order_status",
  "issue_refund", "update_settings", "invite_admin", "remove_admin", "export_data",
];
const STATUSES = ["any", "success", "failed", "flagged"];

// ISO country code -> flag emoji. Renders a regional-indicator pair. Unknown -> globe.
function flagEmoji(code) {
  if (!code || code.length !== 2) return "🌐";
  return code.toUpperCase().replace(/./g, (c) =>
    String.fromCodePoint(127397 + c.charCodeAt(0))
  );
}

function DeviceIcon({ type }) {
  const props = { size: 13 };
  if (type === "mobile") return <Smartphone {...props} />;
  if (type === "tablet") return <Tablet {...props} />;
  if (type === "desktop") return <Monitor {...props} />;
  return <Globe {...props} />;
}

function fmtRelative(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}

function fmtUtc(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + "Z";
  } catch { return iso; }
}

// ---------------------------------------------------------------------------
// Access Log tab
// ---------------------------------------------------------------------------
function AccessLog() {
  const { apiFetch } = useAdminAuth();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({
    actor_email: "", action: "any", target_id: "", status: "any",
    date_from: "", date_to: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "any") params.set(k, v);
    });
    try {
      const { data } = await apiFetch(`/admin/security/logs?${params.toString()}&limit=200`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setMigrationNeeded(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 503 || /schema not migrated/i.test(detail)) {
        setMigrationNeeded(true);
      }
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, apiFetch]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => setFilters({
    actor_email: "", action: "any", target_id: "", status: "any",
    date_from: "", date_to: "",
  });

  const exportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "any") params.set(k, v); });
    try {
      const res = await apiFetch(`/admin/security/logs/export.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dc_access_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed:", e?.response?.data?.detail || e.message);
      toast(e?.response?.data?.detail || "Export failed", "err");
    }
  };

  if (migrationNeeded) {
    return (
      <div className="adm-card" style={{ padding: 24 }} data-testid="security-migration-notice">
        <div style={{ fontFamily: "'Anton', 'Arial Black', Impact, sans-serif", fontSize: 26, letterSpacing: "0.02em", textTransform: "uppercase", marginBottom: 8 }}>
          Migration required
        </div>
        <p style={{ color: "#8B8B93", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
          Run the SQL below in Supabase Studio to enable the access log.
        </p>
        <code style={{ display: "block", background: "#0F0E0D", color: "#EFE9DF", padding: 12, borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
          /app/supabase/migrations/003_admin_security.sql
        </code>
      </div>
    );
  }

  return (
    <div data-testid="access-log-panel">
      {/* Filter bar */}
      <div className="adm-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 10 }}>
          <FilterField label="Actor email">
            <input
              className="adm-input"
              placeholder="admin@..."
              value={filters.actor_email}
              onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })}
              data-testid="filter-actor-email"
            />
          </FilterField>
          <FilterField label="Action">
            <select
              className="adm-select"
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              data-testid="filter-action"
            >
              {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </FilterField>
          <FilterField label="Target">
            <input
              className="adm-input"
              placeholder="ORD-1042 / PRD-007"
              value={filters.target_id}
              onChange={(e) => setFilters({ ...filters, target_id: e.target.value })}
              data-testid="filter-target"
            />
          </FilterField>
          <FilterField label="Status">
            <select
              className="adm-select"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              data-testid="filter-status"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </FilterField>
          <FilterField label="From">
            <input
              type="date"
              className="adm-input"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              data-testid="filter-date-from"
            />
          </FilterField>
          <FilterField label="To">
            <input
              type="date"
              className="adm-input"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              data-testid="filter-date-to"
            />
          </FilterField>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
          <button className="adm-btn adm-btn-ghost" onClick={clearFilters} data-testid="filter-clear">
            <Filter size={13} /> Clear
          </button>
          <button className="adm-btn adm-btn-ghost" onClick={load} data-testid="filter-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
          <div style={{ flex: 1 }} />
          <button className="adm-btn adm-btn-ghost" onClick={exportCsv} data-testid="export-csv">
            <Download size={13} /> Export CSV
          </button>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#8B8B93", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {total} {total === 1 ? "row" : "rows"}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="secTable" data-testid="access-log-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}></th>
                <th>When (UTC)</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>IP</th>
                <th>Location</th>
                <th>Device</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#8B8B93" }}>Loading...</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#8B8B93" }}>No events match the filters.</td></tr>
              )}
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    className={`sec-row sec-row-${r.status}`}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    <td>
                      {expanded === r.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </td>
                    <td className="mono" title={fmtRelative(r.timestamp_utc)}>{fmtUtc(r.timestamp_utc)}</td>
                    <td className="mono">{r.actor_email || "none"}</td>
                    <td><span className="pill">{r.action}</span></td>
                    <td className="mono">{r.target_id || "\u00B7"}</td>
                    <td className="mono">{r.ip || "\u00B7"}</td>
                    <td>
                      <span style={{ marginRight: 6 }}>{flagEmoji(r.geo_country_code)}</span>
                      {[r.geo_city, r.geo_region, r.geo_country].filter(Boolean).join(", ") || "unknown"}
                    </td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <DeviceIcon type={r.device_type} />
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                          {r.browser || "unknown"} / {r.os || ""}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className={`pill pill-${r.status}`}>
                        {r.status === "flagged" && <ShieldAlert size={11} style={{ marginRight: 4 }} />}
                        {r.status}
                      </span>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="sec-row-detail">
                      <td colSpan={9}>
                        <div style={{ padding: "12px 16px", background: "#0F0E0D" }}>
                          <div className="detail-grid">
                            <DetailPair k="Event id" v={r.id} />
                            <DetailPair k="Session id" v={r.session_id || "\u00B7"} />
                            <DetailPair k="Device fingerprint" v={r.device_fingerprint || "\u00B7"} />
                            <DetailPair k="Auth method" v={r.auth_method || "password"} />
                            <DetailPair k="2FA used" v={r.two_fa_used ? "yes" : "no"} />
                            <DetailPair k="Flag reasons" v={(r.flag_reasons || []).join("; ") || "none"} />
                            <DetailPair k="Session duration" v={r.session_duration_seconds ? `${r.session_duration_seconds}s` : "\u00B7"} />
                            <DetailPair k="Full user agent" v={r.user_agent_raw || "\u00B7"} wide />
                          </div>
                          {r.detail && Object.keys(r.detail).length > 0 && (
                            <pre style={{ marginTop: 10, background: "#000", padding: 10, borderRadius: 8, fontSize: 11, color: "#8B8B93", fontFamily: "'JetBrains Mono', monospace", overflow: "auto" }}>
                              {JSON.stringify(r.detail, null, 2)}
                            </pre>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "10px 14px", borderTop: "1px solid #26262C", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#6B6B73", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          <Shield size={11} style={{ verticalAlign: "middle", marginRight: 6 }} />
          Append only. Rows cannot be edited or deleted from the UI.
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#8B8B93", textTransform: "uppercase", letterSpacing: "0.1em" }}>
        {label}
      </span>
      {children}
    </div>
  );
}

function DetailPair({ k, v, wide }) {
  return (
    <div style={{ gridColumn: wide ? "1 / -1" : "auto" }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "#8B8B93", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>
        {k}
      </div>
      <div className="mono" style={{ wordBreak: "break-all", color: "#ECECEE" }}>{v}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active Sessions tab
// ---------------------------------------------------------------------------
function ActiveSessions() {
  const { apiFetch, dcSessionId } = useAdminAuth();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrationNeeded, setMigrationNeeded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch(`/admin/security/sessions?active_only=true`);
      setRows(data.rows || []);
      setMigrationNeeded(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 503 || /schema not migrated/i.test(detail)) {
        setMigrationNeeded(true);
      }
      setRows([]);
    } finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this session? The device will be signed out on its next request.")) return;
    try {
      await apiFetch(`/admin/security/sessions/${id}/revoke`, { method: "POST" });
      toast("Session revoked", "ok");
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Revoke failed", "err");
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Revoke every session except this one?")) return;
    try {
      const { data } = await apiFetch(`/admin/security/sessions/revoke-others`, { method: "POST" });
      toast(`Revoked ${data.revoked_count} session${data.revoked_count === 1 ? "" : "s"}`, "ok");
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Revoke failed", "err");
    }
  };

  if (migrationNeeded) {
    return (
      <div className="adm-card" style={{ padding: 24 }}>
        <p style={{ color: "#8B8B93" }}>Run the migration first (see Access Log tab).</p>
      </div>
    );
  }

  return (
    <div className="adm-card" style={{ padding: 0, overflow: "hidden" }} data-testid="active-sessions-panel">
      <div style={{ padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #26262C" }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#8B8B93", textTransform: "uppercase", letterSpacing: "0.1em" }}>
          {rows.length} active session{rows.length === 1 ? "" : "s"}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="adm-btn adm-btn-ghost" onClick={load}>
            <RefreshCw size={13} /> Refresh
          </button>
          <button className="adm-btn adm-btn-danger" onClick={revokeOthers} data-testid="revoke-others">
            <LogOut size={13} /> Revoke all other sessions
          </button>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="secTable">
          <thead>
            <tr>
              <th>Actor</th>
              <th>2FA</th>
              <th>Device</th>
              <th>IP</th>
              <th>Location</th>
              <th>Started (UTC)</th>
              <th>Last activity</th>
              <th>Session id</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#8B8B93" }}>Loading...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#8B8B93" }}>No active sessions.</td></tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} className={s.id === dcSessionId ? "sec-row sec-row-current" : "sec-row"}>
                <td className="mono">{s.actor_email}{s.id === dcSessionId && <span style={{ marginLeft: 8, color: "#10B981", fontSize: 10 }}>THIS DEVICE</span>}</td>
                <td>
                  {s.mfa_enrolled ? (
                    <span className="pill pill-success">enabled</span>
                  ) : (
                    <span className="pill pill-neutral">not enrolled</span>
                  )}
                </td>
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <DeviceIcon type={s.device_type} />
                    <span className="mono" style={{ fontSize: 11 }}>{s.browser} / {s.os}</span>
                  </span>
                </td>
                <td className="mono">{s.ip || "\u00B7"}</td>
                <td>
                  <span style={{ marginRight: 6 }}>{flagEmoji(s.geo_country_code)}</span>
                  {[s.geo_city, s.geo_country].filter(Boolean).join(", ") || "unknown"}
                </td>
                <td className="mono">{fmtUtc(s.started_at)}</td>
                <td className="mono" title={fmtUtc(s.last_activity_at)}>{fmtRelative(s.last_activity_at)}</td>
                <td className="mono" title={s.id}>{s.id.slice(0, 8)}...</td>
                <td>
                  <button
                    className="adm-btn adm-btn-danger"
                    onClick={() => revoke(s.id)}
                    disabled={s.id === dcSessionId}
                    title={s.id === dcSessionId ? "You can't revoke your own current session; sign out instead." : ""}
                    data-testid={`revoke-${s.id}`}
                  >
                    <XCircle size={12} /> Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root page: stats strip + tabs
// ---------------------------------------------------------------------------
export default function AdminSecurity() {
  const { apiFetch } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("log");

  useEffect(() => {
    apiFetch(`/admin/security/stats`)
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  }, [apiFetch]);

  const flaggedIsHot = (stats?.flagged_events_24h || 0) > 0;

  return (
    <div data-testid="admin-security-page">
      <div className="adm-page-head">
        <div>
          <h1 className="secTitle">Access &amp; Security</h1>
          <p style={{ color: "#8B8B93", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 4 }}>
            <Clock size={11} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Retention {stats?.retention_days ?? 365}d. Idle timeout {stats?.idle_timeout_minutes ?? 30} min. Absolute {stats?.absolute_timeout_hours ?? 12} h.
          </p>
        </div>
      </div>

      {/* Stat strip */}
      <div className="adm-stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="adm-stat" data-testid="stat-active-sessions">
          <div className="adm-stat-label">Active sessions</div>
          <div className="adm-stat-value">{stats?.active_sessions ?? "—"}</div>
        </div>
        <div className="adm-stat" data-testid="stat-logins-24h">
          <div className="adm-stat-label">Logins (24h)</div>
          <div className="adm-stat-value">{stats?.logins_24h ?? "—"}</div>
        </div>
        <div className="adm-stat" data-testid="stat-flagged">
          <div className="adm-stat-label" style={{ color: flaggedIsHot ? "#C42021" : undefined }}>
            <ShieldAlert size={12} style={{ marginRight: 4 }} /> Flagged events (24h)
          </div>
          <div className="adm-stat-value" style={{ color: flaggedIsHot ? "#C42021" : "#EFE9DF" }}>
            {stats?.flagged_events_24h ?? "—"}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="secTabs" role="tablist">
        <button
          className={`secTab ${tab === "log" ? "active" : ""}`}
          onClick={() => setTab("log")}
          data-testid="tab-access-log"
        >
          Access log
        </button>
        <button
          className={`secTab ${tab === "sessions" ? "active" : ""}`}
          onClick={() => setTab("sessions")}
          data-testid="tab-active-sessions"
        >
          Active sessions
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === "log" ? <AccessLog /> : <ActiveSessions />}
      </div>
    </div>
  );
}
